/**
 * Account sidebar nav model.
 *
 * The data + active-item resolver live here as pure functions so they
 * can be unit-tested in the repo's Node-only vitest config without
 * spinning up jsdom + React Testing Library. The visual React shell
 * (apps/storefront/app/account/_lib/AccountNav.tsx) is a thin wrapper
 * around these exports.
 *
 * Order + routes per docs/briefs/customer-portal-account-tier1.md §2.
 * Icons are lucide-react names; the React shell maps strings → components
 * to keep this module serializable.
 */

export type AccountNavItem = {
  label: string
  href: string
  /** lucide-react icon name; React shell resolves to the component. */
  icon: string
}

export const ACCOUNT_NAV_ITEMS: readonly AccountNavItem[] = [
  { label: 'Dashboard', href: '/account',           icon: 'LayoutDashboard' },
  { label: 'Products',  href: '/account/products',  icon: 'Package' },
  { label: 'Orders',    href: '/account/orders',    icon: 'Receipt' },
  { label: 'Warranty',  href: '/account/claims',    icon: 'ShieldCheck' },
  { label: 'Documents', href: '/account/documents', icon: 'FileText' },
  { label: 'Profile',   href: '/account/profile',   icon: 'User' },
] as const

/**
 * Resolve whether a nav item is "active" for the current pathname.
 *
 * Rules:
 *   - /account is active ONLY on exact match. Without this guard,
 *     Dashboard would highlight on every /account/* page because all
 *     of them prefix-match /account.
 *   - Deeper items match by path-segment prefix so /account/products/[id]
 *     keeps Products highlighted while drilled in.
 *   - Trailing slashes are normalized so /account/ and /account both work.
 *   - Sibling routes must not bleed across (e.g. /account/claim singular
 *     must NOT activate /account/claims plural). Implemented by requiring
 *     the next char after the href to be '/' or end-of-string.
 */
export function isAccountNavItemActive(
  pathname: string,
  itemHref: string,
): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'

  if (itemHref === '/account') {
    return normalized === '/account'
  }

  if (normalized === itemHref) return true
  return normalized.startsWith(itemHref + '/')
}
